import type { Command, CommandResult, CommandSink } from '../sim/commands';
import type { Simulation } from '../sim/Simulation';

/**
 * Save / resume for offline matches (D118). The simulation is deterministic,
 * so a save is just the match URL plus every accepted command and the tick it
 * was applied on. Resuming boots the same match and replays the log — the
 * result is the exact same state, with no fragile object-by-object snapshot.
 */
export interface SaveGame {
  readonly v: 1;
  /** The match URL's query (without `resume`), e.g. "?level=3". */
  readonly query: string;
  readonly tick: number;
  readonly label: string;
  readonly at: number;
  readonly log: readonly { readonly t: number; readonly c: Command }[];
}

const KEY = 'atf-save';
/** Beyond this many commands the save stops growing (the match can still be finished, just not resumed). */
export const MAX_LOG = 20_000;

export function matchQuery(search: string): string {
  const p = new URLSearchParams(search);
  p.delete('resume');
  p.sort();
  const q = p.toString();
  return q ? `?${q}` : '';
}

export function loadSave(): SaveGame | null {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) ?? 'null') as SaveGame | null;
    if (!s || s.v !== 1 || typeof s.query !== 'string' || !Array.isArray(s.log) || !Number.isInteger(s.tick)) return null;
    return s;
  } catch {
    return null;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* storage blocked */
  }
}

/** A command sink that also remembers what it accepted, for saving. */
export class RecordingSink implements CommandSink {
  readonly log: { t: number; c: Command }[] = [];
  overflow = false;

  constructor(private readonly sim: Simulation) {}

  submit(cmd: Command): CommandResult {
    const t = this.sim.tick;
    const r = this.sim.apply(cmd);
    if (r.ok) {
      if (this.log.length < MAX_LOG) this.log.push({ t, c: cmd });
      else this.overflow = true;
    }
    return r;
  }

  /** Write the save (no-op once the match is over or the log overflowed). */
  save(query: string, label: string): boolean {
    if (this.overflow || this.sim.match.result) return false;
    const s: SaveGame = { v: 1, query, tick: this.sim.tick, label, at: Date.now(), log: this.log };
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
      return true;
    } catch {
      return false;
    }
  }
}

/** Replay a save into a freshly booted Simulation of the same match. Returns false if it does not fit. */
export function replay(sim: Simulation, save: SaveGame, sink: RecordingSink): boolean {
  if (sim.tick !== 0) return false;
  let i = 0;
  while (sim.tick < save.tick) {
    while (i < save.log.length && save.log[i]!.t === sim.tick) {
      const e = save.log[i++]!;
      sink.submit(e.c);
    }
    sim.step(0.1);
  }
  while (i < save.log.length && save.log[i]!.t === sim.tick) sink.submit(save.log[i++]!.c);
  return i === save.log.length;
}
